<script setup lang="ts">
/**
 * Shown to the installation's admins while a remote support session
 * (Fernwartung) runs: who works on the installation, until when, and the way
 * to end it at once. Renders nothing otherwise. The support operator in the
 * session sees `ShellImpersonationBanner` instead.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { NxBanner, NxButton, formatTime } from '@blpde/nx-ui';
import { useSupportRequests } from '@/support/useSupportRequests';

const { t, locale } = useI18n();
const { active, busy, end, endError } = useSupportRequests();

const text = computed(() => {
  const session = active.value;
  if (!session) return '';
  const operator = session.operatorName || session.operatorEmail;
  const time = formatTime(session.endsAt, { locale: locale.value });
  return time
    ? t('connect.support.banner.running', { operator, time })
    : t('connect.support.banner.runningWithoutEnd', { operator });
});
</script>

<template>
  <div v-if="active" class="cx-support-banner" data-testid="shell-support-banner">
    <NxBanner variant="warning" role="status">
      {{ text }}
      <template #buttons>
        <NxButton variant="outline" type="button" :loading="busy" @click="end(active.id)">
          {{ t('connect.support.banner.end') }}
        </NxButton>
      </template>
    </NxBanner>
    <NxBanner
      v-if="endError"
      variant="danger"
      role="alert"
      data-testid="shell-support-banner-error"
    >
      {{
        t(
          endError === 'connect.support.errors.failed'
            ? 'connect.support.banner.endFailed'
            : endError
        )
      }}
      <template #buttons>
        <NxButton variant="outline" type="button" :loading="busy" @click="end(active.id)">
          {{ t('connect.support.banner.retry') }}
        </NxButton>
      </template>
    </NxBanner>
  </div>
</template>

<style scoped>
.cx-support-banner {
  padding: var(--nxt-space-xs) var(--nxt-space-md) 0;
}
</style>
